import React from "react";
import { Box } from "@mui/material";
import colors from "../../../shared/themes/colors";
import useTranslation from "../../../shared/hooks/useTranslation";
import { ServiceRequest, ServiceRequestFile } from "../models/models";
import ViewServiceRequestSectionCard from "./ViewServiceRequestSectionCard";
import ReportFile from "../../../shared/ReportFile";
import { ImageGallery, useGalleryImages } from "../../../shared/components/gallery";

export interface ViewServiceRequestAttachmentsCardProps {
  serviceRequest: ServiceRequest;
}

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;
const isImageFile = (file: ServiceRequestFile): boolean => {
  return IMAGE_EXTENSIONS.test(file.name) || file.content?.startsWith("data:image/");
};

const ViewServiceRequestAttachmentsCard = ({ serviceRequest }: ViewServiceRequestAttachmentsCardProps) => {
  const { translate } = useTranslation(["servicing", "common"]);

  const files = serviceRequest.files || [];
  const imageFiles = files.filter(isImageFile);
  const nonImageFiles = files.filter((f) => !isImageFile(f));
  const galleryImages = useGalleryImages({ type: "entityFiles", files: imageFiles });

  if (files.length === 0) {
    return null;
  }

  return (
    <ViewServiceRequestSectionCard>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box className="text-18-bold" color={colors.secondary.gray20}>
          {translate("servicing:attachments")}
        </Box>

        {galleryImages.length > 0 && (
          <Box sx={{ mb: nonImageFiles.length > 0 ? 1 : 0 }}>
            <ImageGallery
              images={galleryImages}
              variant="detail"
              showThumbnails
            />
          </Box>
        )}

        {nonImageFiles.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {nonImageFiles.map((file, index) => (
              <ReportFile
                key={index}
                file={file}
                fileIndex={index}
                canDelete={false}
                canDownload={true}
                isNew={!serviceRequest.id}
              />
            ))}
          </Box>
        )}
      </Box>
    </ViewServiceRequestSectionCard>
  );
};

export default ViewServiceRequestAttachmentsCard;
